import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { Box, Dialog, Notice, Stack, Strong, Text } from 'braid-design-system';
import type { ComponentProps, FC, PropsWithChildren } from 'react';
import * as styles from './styles.css';

export type MandatoryQuestions =
  | {
      success: true;
      data: Array<{ question: string; answers: string[] }>;
    }
  | {
      success: false;
    };

interface BillableApplicantsDialogProps {
  hasBillableApplicantsCountError: boolean;
  isOpen: ComponentProps<typeof Dialog>['open'];
  onClose: ComponentProps<typeof Dialog>['onClose'];
  mandatoryQuestions: MandatoryQuestions;
}

export const BillableApplicantsDialog: FC<
  PropsWithChildren<BillableApplicantsDialogProps>
> = ({
  isOpen,
  onClose,
  mandatoryQuestions,
  hasBillableApplicantsCountError,
}) => {
  const { t } = useTranslations(translations);

  return (
    <Dialog
      id="billable-applicants-dialog"
      title={t('Which applications are billable?')}
      closeLabel={t('Close')}
      description={
        <Box paddingTop="small">
          <Stack space="medium">
            {hasBillableApplicantsCountError && (
              <Notice tone="critical">
                <Text>
                  {t(
                    "We can't display the billable applicant unique count right now. Try refreshing the page or check back later.",
                  )}
                </Text>
              </Notice>
            )}
            <Text>
              {t(
                "Applications that have 2 years' or more experience and provide any one of these right to work answers will be marked with a <CustomBadge>Billable</CustomBadge> badge:",
                {
                  CustomBadge: (children) => (
                    <Box
                      title=""
                      display="inlineBlock"
                      background="promoteLight"
                      borderRadius="standard"
                      paddingX="xsmall"
                      className={styles.inlineTextBadge}
                    >
                      {children}
                    </Box>
                  ),
                },
              )}
            </Text>
          </Stack>
        </Box>
      }
      open={isOpen}
      onClose={onClose}
    >
      <Stack space="large">
        {!mandatoryQuestions.success ? (
          <Notice tone="critical">
            <Text>
              {t(
                "We can't load the questions and answers right now. Refresh the page or check back later.",
              )}
            </Text>
          </Notice>
        ) : (
          <Stack space="medium">
            {mandatoryQuestions.data.map(({ answers, question }, index) => (
              <Stack space="small" key={index}>
                <Text weight="strong">{question}</Text>
                {answers.map((answer) => (
                  <Text key={answer}>• {answer}</Text>
                ))}
              </Stack>
            ))}
          </Stack>
        )}
        <Stack space="small">
          <Text size="large" weight="strong">
            {t('How we assess billable applicants')}
          </Text>
          <Text>
            {t(
              "If an application has been marked billable, you'll be charged even if you move that application to the Not Suitable folder.",
            )}
          </Text>
        </Stack>

        <Text>
          <Box className={styles.italicFontStyle}>
            {t("SEEK doesn't verify the answers provided in applications.")}
          </Box>
        </Text>

        <Text>
          {t(
            "If you get multiple applications from the same applicant, you'll only be billed once. <Strong>The Billable Applicant counter shows how many unique applicants have applied to your job ad.</Strong>",
            {
              Strong: (children) => <Strong>{children}</Strong>,
            },
          )}
        </Text>
      </Stack>
    </Dialog>
  );
};
